import { useEffect, useState } from 'react';
import {
  BookChapterRecord,
  downloadManagerService,
  ILessonPlanStep,
  lessonPlanDetailRecord,
  StepData,
  TextBody,
} from '@taleemabad/db';
import { Button } from '../../../atoms/button/button';
import { StyledDiv } from '../../../common/components/styled-div';
import { Typography } from '../../../atoms/typography/typography';
import { Spacing } from '../../../atoms/spacing/spacing';
import { LpStatementList } from '../../../molecules/lp-statement-list/lp-statement-list';
import { TbLpStepsSwitcher } from '../../../molecules/tb-lp-step-switcher/tb-lp-steps-swticher';
import { AlertDialog } from '../../../molecules/dialogs-common/alert-popup';
import { PdfViewer } from '../../../molecules/pdf-viewer/pdf-viewer';
import { LoadingScreen } from '../../../molecules/loading-screen/loading-screen';
import { ChipFilter } from '../../../atoms/chip/chip-filter';
import {
  StackedLpCards,
  LpCardWithHeaderProps,
} from '../../../molecules/lp-card-with-header/lp-card-with-header';
import styled from '@emotion/styled';
import { BreakpointSizes, mq } from '@taleemabad/dsm/themes';
import React from 'react';
import { LpRecordingInviteCard } from '../shared/lp-recording-invite-card';
import { LpRecordingIdleStickyFooter } from '../shared/lp-recording-idle-sticky-footer';
import { LpRecordingStickyFooter } from '../shared/lp-recording-sticky-footer';
import { LpRecordingToast } from '../shared/lp-recording-toast';
import { RecordingActionDialogs } from '../shared/recording-action-dialogs';
import {
  COACHING_RECORDING_CONFIG,
  COACHING_MESSAGES,
  AudioRecorderStatus,
} from '@taleemabad/shared';
import {
  containsUrduCharacters,
  isFlnLessonPlan,
  isStepData,
  removeUrduCharacters,
} from '@taleemabad/dsm/utils';
import { Capacitor } from '@capacitor/core';
import { Network } from '@capacitor/network';

enum Language {
  English = 'en',
  Urdu = 'ur',
}

export interface IGenericLessonPlanStep {
  name: string;
  time: number;
  stepData: ILessonPlanStep[] | StepData[];
}

interface LessonDetailWithSteps {
  steps: IGenericLessonPlanStep[];
  id: number;
  topic: string;
  type: string;
  index: number;
  bookChapter: string;
  schoolClass: string;
  gradeSubject: string;
  book: number;
  videos: string[];
  lpSlo: string[];
  classroomSetupInstructions: ILessonPlanStep[];
  // ? should this be in the main step?
  homeworkInstructions: ILessonPlanStep[];
  summary: ILessonPlanStep[];
  resourcesNeeded: string[];
}

interface LessonPlanViewerProps {
  lpDetail: LessonDetailWithSteps;
  translatedLps?: any;
  isLoading?: boolean;
  showNoInternetPopup?: boolean;
  selectedChapterDetail?: BookChapterRecord;
  isTranslationEnabled?: boolean;
  lessonDetail?: lessonPlanDetailRecord;
  gradeSubjectId?: string;
  onLessonComplete: () => void;
  setShowNoInternetPopup: (value: boolean) => void;
  translateLanguage: (language: string, currentStepName: string) => void;
  onSectionChange?: (section: string) => void;
  // Recording Props
  isRecordingEnabled?: boolean;
  recordingState?: AudioRecorderStatus;
  elapsedLabel?: string;
  elapsedTime?: number;
  onRecordStart?: () => void;
  onRestart?: () => void;
  onPause?: () => void;
  onResume?: () => void;
  showExitConfirmation?: boolean;
  onExitConfirm?: () => void;
  onExitCancel?: () => void;
  showToast?: boolean;
  onCloseToast?: () => void;
  durationLimitReached?: boolean;
}
const StickyTopContainer = styled.div`
  position: sticky;
  top: 0;
  padding-bottom: var(--rk-spacing-xxl);
  background-color: var(--md-sys-color-on-primary);
  z-index: 100;
`;

const StickyRecordPill = styled.div`
  position: absolute;
  right: var(--rk-spacing-xl);
  top: 50%;
  transform: translateY(-50%);
`;
const ChipFilterContainer = styled.div`
  margin: var(--rk-spacing-xl);
  display: flex;
  align-items: center;
`;
interface SwitcherContainerProps {
  isScrolling: boolean;
}

const SwitcherContainer = styled.div<SwitcherContainerProps>`
  padding-bottom: var(--rk-spacing-xxl);
  // only apply padding top if is scrolling is true, since it sticks to the top when scrolling
  padding-top: ${({ isScrolling }) =>
    isScrolling ? 'var(--rk-spacing-lg)' : ''};
`;

interface ButtonContainerProps {
  isLpCompleted: boolean;
  currentStepIndex: number;
}

const ButtonContainer = styled.div<ButtonContainerProps>`
  display: flex;
  justify-content: ${(props) =>
    props.currentStepIndex > 0 && !props.isLpCompleted
      ? 'space-between'
      : 'flex-end'};
  margin-top: var(--rk-spacing-xxxl);
  margin-bottom: var(--rk-spacing-xxxxxxl);

  md-filled-button {
    ${mq(BreakpointSizes.xs)} {
  }
`;

const StickyBottomContainer = styled.div`
  position: sticky;
  bottom: 0;
  z-index: 100;
  background-color: var(--md-sys-color-surface-container-low);
  margin: 0 calc(-1 * var(--rk-spacing-xl));
  padding: var(--rk-spacing-lg) var(--rk-spacing-xl);
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.1);
`;

const LpStatementMemorized = React.memo(LpStatementList);
const LpCardWithHeaderMemorized = React.memo(StackedLpCards);

export const LessonPlanViewer: React.FC<LessonPlanViewerProps> = ({
  lpDetail,
  onLessonComplete,
  translateLanguage,
  translatedLps,
  isLoading,
  showNoInternetPopup = false,
  isTranslationEnabled = false,
  setShowNoInternetPopup,
  onSectionChange,
  lessonDetail,
  isRecordingEnabled = false,
  recordingState = 'idle',
  elapsedLabel = '00:00.0',
  elapsedTime = 0,
  onRecordStart,
  onRestart,
  onPause,
  onResume,
  showExitConfirmation = false,
  onExitConfirm,
  onExitCancel,
  showToast: showToastProp,
  onCloseToast,
  durationLimitReached = false,
}) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);
  const [isRtl, setIsRtl] = useState(false);
  const [defaultLanguage, setDefaultLanguage] = useState<Language>();
  const [language, setLanguage] = useState<Language>(Language.English);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [showSubmitPopup, setShowSubmitPopup] = useState(false);
  const [showRestartConfirmation, setShowRestartConfirmation] = useState(false);

  // Dismiss the internal submit confirmation when the recording limit fires —
  // prevents two dialogs stacking (DSM submit + outer limit dialog).
  useEffect(() => {
    if (durationLimitReached && showSubmitPopup) {
      setShowSubmitPopup(false);
    }
  }, [durationLimitReached, showSubmitPopup]);

  const isPdfLesson = !!lessonDetail?.lpPdfLink;

  const isFln = isFlnLessonPlan(lpDetail.steps);

  useEffect(() => {
    if (showToastProp && !toastMessage) {
      setToastMessage('Recording started');
    }
  }, [showToastProp, toastMessage]);

  useEffect(() => {
    const pdfLink = lessonDetail?.lpPdfLink;
    const loadPdf = async () => {
      if (!pdfLink) {
        setPdfUrl(null);
        return;
      }
      if (
        Capacitor.isNativePlatform() &&
        !(await Network.getStatus()).connected
      ) {
        const downloadedPdf =
          await downloadManagerService.getDownloadURL(pdfLink);
        setPdfUrl(downloadedPdf);
      } else {
        setPdfUrl(pdfLink);
      }
    };
    loadPdf();
  }, [lessonDetail]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolling(window.scrollY > 0);
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  const determineLanguageAndDirection = () => {
    const isUrdu = containsUrduCharacters(lpDetail.topic);
    return {
      language: isUrdu ? Language.Urdu : Language.English,
      isRtl: isUrdu,
    };
  };

  useEffect(() => {
    const { language, isRtl } = determineLanguageAndDirection();
    setDefaultLanguage(language);
    setLanguage(language);
    setIsRtl(isRtl);
  }, [lpDetail.topic]);

  useEffect(() => {
    language === Language.Urdu ? setIsRtl(true) : setIsRtl(false);
    if (
      !translatedLps &&
      language !== defaultLanguage &&
      lpDetail.steps.length > 0
    ) {
      const currentStep = lpDetail.steps[currentStepIndex];
      if (currentStep?.name) {
        translateLanguage(language, currentStep.name);
      }
    }
  }, [language, defaultLanguage, translatedLps]);
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleNextStep = () => {
    setCurrentStepIndex((prevIndex) => {
      const newIndex = Math.min(prevIndex + 1, lpDetail.steps.length - 1);
      scrollToTop();
      return newIndex;
    });
  };
  const handlePreviousStep = () => {
    setCurrentStepIndex((prevIndex) => {
      const newIndex = Math.max(prevIndex - 1, 0);
      scrollToTop();
      return newIndex;
    });
  };

  const currentStep = lpDetail.steps[currentStepIndex];
  const isLastStep = currentStepIndex === lpDetail.steps.length - 1;
  const translatedStep = translatedLps?.steps[currentStepIndex];
  const handleChangeStep = (direction: string) => {
    if (direction === 'next') {
      handleNextStep();
      onSectionChange?.(lpDetail.steps[currentStepIndex + 1]?.name);
    } else if (direction === 'prev') {
      handlePreviousStep();
      onSectionChange?.(lpDetail.steps[currentStepIndex - 1]?.name);
    }
  };

  const handleConfirmSubmit = () => {
    setShowSubmitPopup(false);
    onLessonComplete();
  };

  const handleLessonCompleteClick = () => {
    if (recordingState !== 'idle') {
      if (elapsedTime < COACHING_RECORDING_CONFIG.MIN_DURATION_MS) {
        onLessonComplete();
      } else {
        setShowSubmitPopup(true);
      }
    } else {
      onLessonComplete();
    }
  };

  const handleButtonClick = async () => {
    if (isPdfLesson || isLastStep) {
      handleLessonCompleteClick();
    } else {
      handleNextStep();
    }
    onSectionChange?.(lpDetail.steps[currentStepIndex + 1]?.name);
  };
  const transformStepDataToCards = (
    stepData: StepData[] | ILessonPlanStep[] | undefined,
  ): LpCardWithHeaderProps[] => {
    if (!stepData || !Array.isArray(stepData)) return [];

    return stepData.map((step: StepData | ILessonPlanStep) => {
      if (isStepData(step)) {
        return {
          id: step.index,
          preTitle: step.header,
          textBody: step.textBody.map((item: TextBody) => ({
            statement: item.statement,
            media: item.media ?? [],
          })),
          level: step.level ?? null,
        };
      } else {
        return {
          id: step.index,
          preTitle: 'Homework',
          textBody: [
            {
              statement: step.statement,
              media: step.media ?? [],
            },
          ],
        };
      }
    });
  };

  if (isPdfLesson) {
    return (
      <>
        {isLoading && <LoadingScreen />}
        <StyledDiv
          padding="var(--rk-spacing-md)"
          flexDirection="column"
          height="calc(100vh - 100px)"
        >
          {pdfUrl ? (
            <PdfViewer
              url={pdfUrl}
              fileName={`lesson-plan-${lpDetail.id}.pdf`}
            />
          ) : (
            <Typography variant="textBodyLarge">
              No PDF URL provided.
            </Typography>
          )}
          <StyledDiv justifyContent="flex-end" marginTop={32}>
            <Button variant="filled" onClick={handleButtonClick}>
              Lesson Complete
            </Button>
          </StyledDiv>
        </StyledDiv>
      </>
    );
  }

  if (!lpDetail?.steps?.length) {
    return (
      <Typography variant="textBodyLarge">
        No lesson plan steps available
      </Typography>
    );
  }

  return (
    <>
      {isLoading && <LoadingScreen />}
      <RecordingActionDialogs
        showSubmitPopup={showSubmitPopup}
        onCloseSubmitPopup={() => setShowSubmitPopup(false)}
        onConfirmSubmit={handleConfirmSubmit}
        showRestartPopup={showRestartConfirmation}
        onCloseRestartPopup={() => setShowRestartConfirmation(false)}
        onConfirmRestart={() => {
          setToastMessage('Recording restarted');
          onRestart?.();
          setShowRestartConfirmation(false);
          setShowToast(true);
        }}
        showExitPopup={showExitConfirmation}
        onCloseExitPopup={() => onExitCancel?.()}
        onConfirmExit={() => onExitConfirm?.()}
        exitTitle={
          elapsedTime >= COACHING_RECORDING_CONFIG.MIN_DURATION_MS
            ? 'Leave without submitting?'
            : 'Exit while Recording?'
        }
        exitDescription={
          elapsedTime >= COACHING_RECORDING_CONFIG.MIN_DURATION_MS
            ? 'Your recording is saved but not submitted yet. You can submit it later.'
            : "Your recording will be discarded and won't be saved. Are you sure you want to exit?"
        }
      />
      <LpRecordingToast
        message={toastMessage}
        show={showToast || !!showToastProp}
        onClose={() => {
          setShowToast(false);
          onCloseToast?.();
        }}
      />

      <AlertDialog
        title="No Internet Connection"
        description="Seems like your internet is not connected. Connect and try again."
        showPopup={showNoInternetPopup}
        onClosePopup={() => {
          setLanguage(defaultLanguage ?? Language.English);
          setShowNoInternetPopup(false);
        }}
      />

      {!showNoInternetPopup && (
        <>
          {isTranslationEnabled && (
            <ChipFilterContainer>
              <Typography
                variant="textBodyLargeOnSurface"
                data-cy="translate-heading"
              >
                Translate in:{' '}
              </Typography>
              <Spacing margin="none md none md">
                <ChipFilter
                  label="English"
                  selected={language === Language.English}
                  onClick={() => setLanguage(Language.English)}
                  alwaysFocusable={true}
                  data-cy="translate-chip-english"
                />
              </Spacing>
              <Spacing margin="none md none md">
                <ChipFilter
                  label="Urdu"
                  selected={language === Language.Urdu}
                  onClick={() => setLanguage(Language.Urdu)}
                  data-cy="translate-chip-urdu"
                />
              </Spacing>
            </ChipFilterContainer>
          )}

          <StickyTopContainer>
            <SwitcherContainer isScrolling={isScrolling}>
              <TbLpStepsSwitcher
                stepName={currentStep?.name ?? ''}
                stepTime={currentStep?.time ?? 0}
                isLeftIconActive={currentStepIndex > 0}
                isRightIconActive={currentStepIndex < lpDetail.steps.length - 1}
                isScrolling={isScrolling}
                onChange={handleChangeStep}
              />
              {isScrolling &&
                isRecordingEnabled &&
                recordingState === 'idle' &&
                onRecordStart && <div />}
            </SwitcherContainer>
            <Typography variant="textHeadlineSmallPrimary">
              {removeUrduCharacters(lpDetail.topic)}
            </Typography>
          </StickyTopContainer>
          {isFln && (
            <LpCardWithHeaderMemorized
              cards={transformStepDataToCards(
                language === defaultLanguage
                  ? (currentStep.stepData as StepData[])
                  : translatedStep?.stepData,
              )}
            />
          )}
          {!isFln && (
            <LpStatementMemorized
              id={lpDetail.id}
              statements={
                language === defaultLanguage
                  ? (currentStep.stepData as ILessonPlanStep[])
                  : translatedStep?.stepData
              }
              isRtl={isRtl}
            />
          )}
          <ButtonContainer
            currentStepIndex={currentStepIndex}
            isLpCompleted={isLastStep}
          >
            {currentStepIndex > 0 && !isLastStep && (
              <Button
                variant="outlined"
                onClick={() => handleChangeStep('prev')}
                data-cy="lesson-view-back-button"
              >
                Back
              </Button>
            )}
            <Button
              variant="filled"
              onClick={handleButtonClick}
              data-cy={
                isLastStep
                  ? 'lesson-view-complete-button'
                  : 'lesson-view-next-button'
              }
            >
              {isLastStep
                ? recordingState !== 'idle'
                  ? 'Complete Lesson & Submit Recording'
                  : 'Lesson Complete'
                : 'Next'}
            </Button>
          </ButtonContainer>

          {isRecordingEnabled && recordingState === 'idle' && onRecordStart && (
            <LpRecordingIdleStickyFooter
              position="bottom"
              onRecordStart={() => {
                setToastMessage('Recording started');
                onRecordStart();
                setShowToast(true);
              }}
            />
          )}

          {(recordingState !== 'idle' || durationLimitReached) &&
            isRecordingEnabled &&
            onRestart &&
            onPause &&
            onResume && (
              <LpRecordingStickyFooter
                position="bottom"
                elapsedLabel={elapsedLabel}
                isPaused={recordingState === 'paused'}
                isFinished={
                  durationLimitReached ||
                  elapsedTime >= COACHING_RECORDING_CONFIG.MAX_DURATION_MS
                }
                onRestart={() => setShowRestartConfirmation(true)}
                onPause={onPause}
                onResume={onResume}
              />
            )}
        </>
      )}
    </>
  );
};
